import React, { useContext, useEffect, useMemo } from 'react';
import { PromiseState } from 'react-refetch';
import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import getDiscoveryKPIURL from 'url-constructors/Shazam/getDiscoveryKPIUrl';
import getDiscoveriesTrendUrl from 'url-constructors/Shazam/getDiscoveriesTrendUrl';
import getDiscoveryKPICountries from 'url-constructors/Shazam/getDiscoveryKPICountries';
import metrics from 'maps/metrics';
import first from 'lodash.first';
import delve from 'dlv';
import { TimeFrames } from 'components/TopicFilters/shared';
import OverviewContainer from 'components/Overview/Container/Container';
import useUrlForTopic from '../shared/utils/useUrlForTopic';
import ShazamOverviewKPIs from './ShazamOverviewKPIs';
import useMedia from 'hooks/useMedia';
import useCountryDiscoveryData from 'components/data-containers/KPIs/Discovery/utils/useDiscoveryCountriesData';
import getDiscoveryKPICities from 'url-constructors/Shazam/getDiscoveryKPICities';
import useDiscoveryCityData from 'components/data-containers/KPIs/Discovery/utils/useDiscoveryCitiesData';
import Box from 'components/elements/Box';
import { GridBox, TrendChartWrapper } from './ShazamOverview.styles';
import ShazamOverviewTerritories from './ShazamOverviewTerritories';
import TrendChart from '../shared/TrendChart';

function dataSources(): any {
  return {
    fetchTrendData: (args: any): any => ({
      trendData: getDiscoveriesTrendUrl(args),
    }),
    fetchKPIData: (args: any): any => ({
      kpiData: getDiscoveryKPIURL(args),
    }),
    fetchTopCountriesData: (args: any): any => ({
      topCountries: getDiscoveryKPICountries(args),
    }),
    fetchTopCitiesData: (args: any): any => ({
      topCities: getDiscoveryKPICities(args),
    }),
  };
}

function getTimeframes(
  globalSortBy: TimeFrames
): { current: string; prev: string } {
  switch (globalSortBy) {
    case 'lw':
      return { current: 'countLastWeek', prev: 'countLastLastWeek' };
    case 'twtd':
    default:
      return { current: 'countThisWeekToDate', prev: 'countLastWeekToDate' };
  }
}

interface IShazamOverviewProps {
  fetchKPIData: (args: any) => { kpiData: string };
  fetchTrendData: (args: any) => { trendData: string };
  fetchTopCountriesData: (args: any) => { topCountries: string };
  fetchTopCitiesData: (args: any) => { topCities: string };
  kpiData: PromiseState<any>;
  trendData: PromiseState<any>;
  topCountries: PromiseState<any>;
  topCities: PromiseState<any>;
}

const ShazamOverview: React.FC<IShazamOverviewProps> = (props) => {
  const {
    fetchTrendData,
    fetchKPIData,
    fetchTopCountriesData,
    fetchTopCitiesData,
    trendData = PromiseState.create(),
    kpiData = PromiseState.create(),
    topCountries = PromiseState.create(),
    topCities = PromiseState.create(),
  } = props;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    countryIds,
    filters,
    viewSettings,
    compareMode,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';
  const { current, prev } = getTimeframes(timeframe);
  const sortByMetric = metrics[current];

  const countryFiltering = filters?.country || '';

  const isMobileView = useMedia('(max-width: 40em)');

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      countryIds: filters?.country,
      region: filters?.region,
      customRegion: customRegionFilter,
      compareMode,
    };

    fetchKPIData(args);

    fetchTrendData({
      ...args,
      period: 'day',
    });

    fetchTopCountriesData({
      ...args,
      limit: 5,
      sortBy: current,
      sortOrder: 'desc',
    });

    fetchTopCitiesData({
      ...args,
      limit: 5,
      sortBy: current,
      sortOrder: 'desc',
      countryIds: filters?.country,
    });
  }, [
    countryIds,
    entityIds,
    entityType,
    fetchKPIData,
    fetchTrendData,
    fetchTopCountriesData,
    fetchTopCitiesData,
    filters,
    current,
    compareMode,
    customRegionFilter,
  ]);

  const [, isPending] = useCombinedRefetchState([
    kpiData,
    trendData,
    topCountries,
    topCities,
  ]);

  const topCountriesData = useCountryDiscoveryData(topCountries.value);
  const topCitiesData = useDiscoveryCityData(topCities.value);

  const kpis: any = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const currentValue = delve(kpis, ['data', sortByMetric.value], null);
  const change = sortByMetric.change
    ? delve(kpis, ['data', sortByMetric.change], null)
    : 0;
  const previousValue = delve(kpis, ['data', prev], null);

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Shazam"
      linkTo={useUrlForTopic('airplay/discoveries')}
      short
      isNotPadded
      isMobile={isMobileView}
    >
      <GridBox>
        <Box pb={isMobileView ? 5 : 0} flex="1 0 50%">
          <ShazamOverviewKPIs
            isMobileView={isMobileView}
            title={sortByMetric.label}
            current={currentValue}
            change={change}
            previous={previousValue}
            isPending={isPending}
          />
          {!isMobileView && (
            <TrendChartWrapper>
              <TrendChart isPending={isPending} trendData={trendData} />
            </TrendChartWrapper>
          )}
        </Box>
        <Box flex="1 0 50%">
          <ShazamOverviewTerritories
            isPending={isPending}
            topCitiesData={topCitiesData}
            topCountriesData={topCountriesData}
            countryFiltering={countryFiltering}
          />
        </Box>
        {isMobileView && (
          <TrendChartWrapper>
            <TrendChart isPending={isPending} trendData={trendData} />
          </TrendChartWrapper>
        )}
      </GridBox>
    </OverviewContainer>
  );
};

export default connect<IShazamOverviewProps>(dataSources)(
  ShazamOverview
) as any;
